import type { InputFieldConfig } from "../types/forms.types";

import * as validators from "../validation/validators";
import {
  normalizeString,
  normalizeNumber,
  normalizeEmail,
  normalizeLogin,
  normalizeLocalTime,
  normalizeWeather,
} from "../utils/normalize.utils";

const inputFields: Record<string, InputFieldConfig> = {
  username: {
    id: "username",
    name: "username",
    label: "Username",
    type: "text",
    placeholder: "Your username",
    validator: validators.validateUsername,
    normalizator: normalizeString,
  },
  email: {
    id: "email",
    name: "email",
    label: "Email",
    type: "email",
    placeholder: "your@email.com",
    validator: validators.validateEmail,
    normalizator: normalizeEmail,
  },
  password: {
    id: "password",
    name: "password",
    label: "Password",
    type: "password",
    placeholder: "Your password",
    validator: validators.validatePassword,
    normalizator: normalizeString,
  },
  confirmPassword: {
    id: "confirmPassword",
    name: "confirmPassword",
    label: "Confirm password",
    type: "password",
    placeholder: "Re-enter password",
    validator: validators.validateConfirmPassword,
    normalizator: normalizeString,
  },
  login: {
    id: "login",
    name: "login",
    label: "Login",
    type: "text",
    placeholder: "Your username or email",
    validator: validators.validateLogin,
    normalizator: normalizeLogin,
  },
  firstName: {
    id: "firstName",
    name: "firstName",
    label: "First Name",
    type: "text",
    placeholder: "Your first name",
    validator: validators.validateName,
    normalizator: normalizeString,
  },
  lastName: {
    id: "lastName",
    name: "lastName",
    label: "Last Name",
    type: "text",
    placeholder: "Your last name",
    validator: validators.validateName,
    normalizator: normalizeString,
  },
  dateOfBirth: {
    id: "dateOfBirth",
    name: "dateOfBirth",
    label: "Date of birth",
    type: "date",
    placeholder: "YYYY-MM-DD",
    normalizator: normalizeString,
  },
  heightCm: {
    id: "heightCm",
    name: "heightCm",
    label: "Height, cm",
    type: "number",
    placeholder: "e.g. 175",
    min: 1,
    max: 300,
    step: 1,
    normalizator: normalizeNumber,
  },
  weightKg: {
    id: "weightKg",
    name: "weightKg",
    label: "Weight, kg",
    type: "number",
    placeholder: "e.g. 70",
    min: 1,
    max: 300,
    step: 1,
    normalizator: normalizeNumber,
  },
  startTime: {
    id: "startTime",
    name: "startTime",
    label: "Run start date and time *",
    type: "datetime-local",
    placeholder: "YYYY-MM-DD hh:mm",
    validator: validators.validateStartTime,
    normalizator: normalizeLocalTime,
  },
  durationH: {
    id: "durationH",
    name: "durationH",
    label: "Hours",
    type: "number",
    placeholder: "h",
    min: 0,
    max: 999,
    step: 1,
    validator: validators.validateDuration,
    normalizator: normalizeNumber,
  },
  durationM: {
    id: "durationM",
    name: "durationM",
    label: "Minutes",
    type: "number",
    placeholder: "m",
    min: 0,
    max: 59,
    step: 1,
    normalizator: normalizeNumber,
  },
  durationS: {
    id: "durationS",
    name: "durationS",
    label: "Seconds",
    type: "number",
    placeholder: "s",
    min: 0,
    max: 59,
    step: 1,
    normalizator: normalizeNumber,
  },
  distanceKm: {
    id: "distanceKm",
    name: "distanceKm",
    label: "Distance (km) *",
    type: "number",
    placeholder: "e.g. 5.0",
    min: 0.01,
    max: 999,
    step: 1,
    validator: validators.validateDistance,
    normalizator: normalizeNumber,
  },
  title: {
    id: "title",
    name: "title",
    label: "Run title",
    type: "text",
    placeholder: "Morning easy run",
    normalizator: normalizeString,
  },
  notes: {
    id: "notes",
    name: "notes",
    label: "Notes",
    type: "textarea",
    placeholder: "How did it feel?",
    normalizator: normalizeString,
  },
  perceivedEffort: {
    id: "perceivedEffort",
    name: "perceivedEffort",
    label: "Effort Level:",
    type: "range",
    min: 1,
    max: 10,
    step: 1,
    normalizator: normalizeNumber,
  },
  weatherSunny: {
    id: "weatherSunny",
    name: "weather",
    value: "sunny",
    label: "Sunny",
    type: "radio",
    normalizator: normalizeWeather,
  },
  weatherPartlyCloudy: {
    id: "weatherPartlyCloudy",
    name: "weather",
    value: "partly_cloudy",
    label: "Partly cloudy",
    type: "radio",
    normalizator: normalizeWeather,
  },
  weatherCloudy: {
    id: "weatherCloudy",
    name: "weather",
    value: "cloudy",
    label: "Cloudy",
    type: "radio",
    normalizator: normalizeWeather,
  },
  weatherRain: {
    id: "weatherRain",
    name: "weather",
    value: "rain",
    label: "Rain",
    type: "radio",
    normalizator: normalizeWeather,
  },
  weatherSnow: {
    id: "weatherSnow",
    name: "weather",
    value: "snow",
    label: "Snow",
    type: "radio",
    normalizator: normalizeWeather,
  },
  weatherWindy: {
    id: "weatherWindy",
    name: "weather",
    value: "windy",
    label: "Windy",
    type: "radio",
    normalizator: normalizeWeather,
  },
  weatherHot: {
    id: "weatherHot",
    name: "weather",
    value: "hot",
    label: "Hot",
    type: "radio",
    normalizator: normalizeWeather,
  },
  weatherCold: {
    id: "weatherCold",
    name: "weather",
    value: "cold",
    label: "Cold",
    type: "radio",
    normalizator: normalizeWeather,
  },
};

export { inputFields };
